import React, { useCallback } from 'react';
import { Input, Popconfirm, Form, Space, Col } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { PlusOutlined } from '@ant-design/icons';
import { sumBy } from 'lodash';

import { toCurrency, toRate } from 'utils/utils';
import { useFormList } from 'hooks/useFormList';
import { shouldUpdateCompare } from 'utils/utils2';
import Value from 'components/Value';
import DataGrid from 'components/DataGrid';
import TaxSelect from 'components/TaxSelect';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { ClientInvoiceLineResponse } from 'services/clientInvoice';
import ContractListSelect from '../../../../components/ContractListSelect';
import InputCurrency from 'components/InputCurrency';
import DeleteButton from 'components/DeleteButton';
import Title from 'components/Title';
import ButtonOperation from 'components/ButtonOperation';
import { useDisplayFrameworkContext } from 'DisplayFramework/hooks/useDisplayFrameworkContext';
import GetLabel from 'DisplayFramework/components/GetLabel';
import _ from 'lodash';

type TabClientInvoiceLinesProps = {
  form: FormInstance;
  ciid: number | undefined;
  cimultiplecontracts: boolean;
};

const TabClientInvoiceLines = (props: TabClientInvoiceLinesProps) => {
  const { form, ciid, cimultiplecontracts } = props;

  const fieldName = 'clientInvoiceLines';

  const { getComponentConfiguration } = useDisplayFrameworkContext();

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'cilid',
  });

  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }

  const addRow = () => {
    const item: ClientInvoiceLineResponse = {
      cilid:Math.round(_.random(10000000)),
      ciltype: 'FEES',
      cilcategory: '',
      cildescription: '',
      cilnetamount: 0,
      ciltaxcode: '',
      ciltaxrate: 0,
      ciltaxamount: 0,
      cilgrossamount: 0,
      ctreference: '',
      ctrid: 0,
      psid: 0,
      psorder: 0,
      ciamortizedamount: 0,
      ciidinvoicedfees: 0,
      ciidlateinterestfees: 0,
      cildescription2: '',
      cilinterestamount: 0,
      cilorder: 0,
    };

    add(item);
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(
        fieldName
      ) as ClientInvoiceLineResponse[];

      const newValues = values.map((item, i) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const cilnetamount = updatedItem.cilnetamount;
        const ciltaxamount = cilnetamount * (updatedItem.ciltaxrate / 100);
        const cilgrossamount = cilnetamount + ciltaxamount;

        return {
          ...updatedItem,
          cilnetamount,
          ciltaxamount,
          cilgrossamount,
        };
      });

      const cinettotal = sumBy(newValues, 'cilnetamount');
      const citaxtotal = sumBy(newValues, 'ciltaxamount');
      const cigrosstotal = sumBy(newValues, 'cilgrossamount');

      form.setFieldsValue({
        [fieldName]: newValues,
        cinettotal,
        citaxtotal,
        cigrosstotal,
      });
    },
    [form]
  );

  const columnRequired = (comcode: string): boolean => {
    let comp = getComponentConfiguration(comcode);
    return comp?.flagrequired ?? false;
  };

  const columns: DataGridColDef<ClientInvoiceLineResponse>[] = [
    {
      comcode: 'CILines.ciltype',
      key: 'ciltype',
      fixed: 'left',
      width: '10%',
      render: (_, record, index) => (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            let citype = getFieldValue('citype');
            if (citype === 'FIRSTRENT') {
              citype = 'RENTAL';
            }
            return (
              <Form.Item
                name={[fieldName, index, 'ciltype']}
                rules={[{ required: columnRequired('CILines.ciltype') }]}
              >
                <ParListSelect
                  parcode="CILTYPE"
                  parvalueparent={citype}
                  disabled={ciid !== undefined}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      ),
    },
    /*    {
      comcode: 'CILines.cilcategory',
      key: 'cilcategory',
      width: 150,
      render: (_, record, index) => (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => (
            <Form.Item
              name={[fieldName, index, 'cilcategory']}
              rules={[{ required: true }]}
            >
              <ParListSelect
                parcode="CILCAT"
                parvalueparent={getFieldValue([fieldName, index, 'ciltype'])}
              />
            </Form.Item>
          )}
        </Form.Item>
      ),
    },*/
    {
      comcode: 'CILines.cildescription',
      key: 'cildescription',
      width: '20%',
      fixed: 'left',
      render: (_value, _record, index) => (
        <Form.Item
          name={[fieldName, index, 'cildescription']}
          rules={[{ required: columnRequired('CILines.cildescription') }]}
        >
          <Input disabled={ciid !== undefined} />
        </Form.Item>
      ),
    },
    {
      comcode: 'CILines.cildescription2',
      key: 'cildescription2',
      width: '20%',
      render: (_value, _record, index) => (
        <Form.Item
          name={[fieldName, index, 'cildescription2']}
          rules={[{ required: columnRequired('CILines.cildescription2') }]}
          initialValue={form.getFieldValue([
            fieldName,
            index,
            'cildescription',
          ])}
        >
          <Input
            disabled={ciid !== undefined}
            value={form.getFieldValue([fieldName, index, 'cildescription'])}
          />
        </Form.Item>
      ),
    },
    {
      comcode: 'CILines.cilnetamount',
      dataIndex: 'cilnetamount',
      width: '10%',
      fixed: 'right',
      render: (_value, _record, index) => (
        <Form.Item
          name={[fieldName, index, 'cilnetamount']}
          rules={[{ required: columnRequired('CILines.cilnetamount') }]}
        >
          <InputCurrency
            currcode={form.getFieldValue('currcode')}
            onChange={(value) => recalculate(index, { cilnetamount: value })}
            disabled={ciid !== undefined}
          />
        </Form.Item>
      ),
    },
    {
      comcode: 'CILines.ciltaxcode',
      key: 'ciltaxcode',
      width: '8%',
      fixed: 'right',
      render: (_, record, index) => (
        <Form.Item
          name={[fieldName, index, 'ciltaxcode']}
          rules={[{ required: columnRequired('CILines.ciltaxcode') }]}
        >
          <TaxSelect
            taxflow="INOUT"
            onSelected={(tax) =>
              recalculate(index, { ciltaxrate: tax?.taxrate ?? 0 })
            }
            disabled={ciid !== undefined}
          />
        </Form.Item>
      ),
    },
    {
      comcode: 'CILines.ciltaxrate',
      dataIndex: 'ciltaxrate',
      width: '5%',
      fixed: 'right',
      render: (_value, _record, index) => (
        <Form.Item name={[fieldName, index, 'ciltaxrate']}>
          <Value formatter={toRate} />
        </Form.Item>
      ),
    },
    {
      comcode: 'CILines.ciltaxamount',
      key: 'comptaxamount',
      fixed: 'right',
      render: (_value, _record, index) => (
        <Form.Item name={[fieldName, index, 'ciltaxamount']}>
          <Value
            formatter={(value) =>
              toCurrency(value, form.getFieldValue('currcode'))
            }
          />
        </Form.Item>
      ),
      width: '8%',
    },
    {
      comcode: 'CILines.cilgrossamount',
      key: 'cilgrossamount',

      render: (_value, _record, index) => (
        <Form.Item name={[fieldName, index, 'cilgrossamount']}>
          <Value
            formatter={(value) =>
              toCurrency(value, form.getFieldValue('currcode'))
            }
          />
        </Form.Item>
      ),
      fixed: 'right',
      width: '7%',
    },
  ];

  if (ciid === undefined) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <Popconfirm
          title="Sure to delete?"
          onConfirm={() => {
            remove(record.cilid);
          }}
        >
          <DeleteButton onlyIcon />
        </Popconfirm>
      ),
      align: 'center',
      width: '5%',
    });
  }

  if (cimultiplecontracts) {
    columns.unshift({
      comcode: 'CILines.ctrid',
      key: 'ctrid',
      width: '15%',
      render: (_value, _record, index) => (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => (
            <Form.Item name={[fieldName, index, 'ctrid']}>
              <ContractListSelect
                tpreference={getFieldValue(['thirdparty', 'tpreference'])}
                form={form}
                fieldName={`${fieldName}.${index}.ctreference`}
              />
            </Form.Item>
          )}
        </Form.Item>
      ),
    });
  }

  const title = (
    <>
      <Col span={17}></Col>
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare([
          'cinettotal',
          'citaxtotal',
          'cigrosstotal',
          'currcode',
        ])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
                <GetLabel label="Net" />{' '}
                {toCurrency(
                  getFieldValue('cinettotal'),
                  form.getFieldValue('currcode')
                )}
              </span>
              <span style={{ color: '#b3c6ff' }}>
                <GetLabel label="Tax" />{' '}
                {toCurrency(
                  getFieldValue('citaxtotal'),
                  form.getFieldValue('currcode')
                )}
              </span>
              <strong style={{ color: '#668cff' }}>
                <GetLabel label="Gross" />{' '}
                {toCurrency(
                  getFieldValue('cigrosstotal'),
                  form.getFieldValue('currcode')
                )}
              </strong>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  return (
    <div style={{ backgroundColor: '#f7f7f7', padding: 5 }}>
      <Title comcode="InvoiceDetails" level={5}></Title>
      <div style={{ padding: 5, background: 'white' }}>
        <div>
          <Form.Item
            noStyle
            shouldUpdate={shouldUpdateCompare(['clientInvoiceLines'])}
          >
            {({ getFieldValue }) => (
              <>
                {ciid === undefined ? (
                  <DataGrid
                    title={title}
                    columns={columns}
                    dataSource={getFieldValue(fieldName)}
                    size="small"
                    rowKey="cilid"
                    hideFilters
                    extraActions={
                      <ButtonOperation
                        icon={<PlusOutlined />}
                        type="primary"
                        onClick={addRow}
                        comcode="add"
                      />
                    }
                  />
                ) : (
                  <DataGrid
                    title={title}
                    columns={columns}
                    hideFilters
                    dataSource={getFieldValue(fieldName)}
                    size="small"
                    rowKey="cilid"
                    scroll={{ x: 1000, y: 360 }}
                    pagination={{ pageSize: 200 }}
                  />
                )}{' '}
              </>
            )}
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export default TabClientInvoiceLines;
